import { Link, useNavigate } from "react-router";
import { DashboardCard } from "../molecules/DashboardCard";
import { DueDateCell } from "../molecules/DueDateCell";
import { DocumentVersionStatus } from "~/config/types";
import { dateDiff } from "~/helpers/date";
import { useTranslation } from "react-i18next";
import NoResult from "../molecules/NoResult";
import SimpleDataTable, {
  type TableColumn,
} from "../molecules/SimpleDataTable";
import { CenterSpinner } from "../atoms/spinner";
import { Button } from "~/atoms/button";
import { useWorkflows } from "~/api/v2/useWorkflows";
import { type WorkflowListItem } from "~/api/types";
import { WorkflowProgress } from "~/molecules/WorkflowProgress";

const OngoingWorkflowsContainer = () => {
  const { t } = useTranslation();

  const tableColumns: TableColumn<WorkflowListItem>[] = [
    {
      header: t("dashboard.column.title"),
      compareFn: (a, b) =>
        a.documentVersion.title.localeCompare(b.documentVersion.title),
      cell: (v) => <span>{v.documentVersion.title}</span>,
    },
    {
      header: t("commons.workflow.progress"),
      compareFn: (a, b) =>
        a.completedSteps / a.totalSteps - b.completedSteps / b.totalSteps,
      cell: (wf) => (
        <WorkflowProgress
          completedSteps={wf.completedSteps}
          totalSteps={wf.totalSteps}
        />
      ),
      tableHeadClassName: "w-40",
    },
    {
      header: t("dashboard.column.dueDate"),
      defaultSort: true,
      compareFn: (a, b) =>
        dateDiff(
          a.documentVersion.reviewTo ?? a.document.scheduleReviewTo,
          b.documentVersion.reviewTo ?? b.document.scheduleReviewTo,
        ),
      cell: (wf) => (
        <DueDateCell
          dueDate={wf.documentVersion.reviewTo ?? wf.document.scheduleReviewTo}
        />
      ),
      tableHeadClassName: "w-40",
    },
  ];
  const navigate = useNavigate();
  const { data: workflows, isLoading: isLoadingWorkflows } = useWorkflows();

  const activeWorkflows = workflows?.filter(
    (wf) =>
      wf.isOngoing &&
      wf.documentVersion.status !== DocumentVersionStatus.archived &&
      wf.documentVersion.status !== DocumentVersionStatus.published,
  );

  const containerTitle = t("dashboard.ongoing.workflows");

  if (isLoadingWorkflows) {
    return (
      <DashboardCard title={containerTitle} className="h-80">
        <CenterSpinner />
      </DashboardCard>
    );
  }

  if (activeWorkflows?.length === 0) {
    return (
      <DashboardCard title={containerTitle}>
        <NoResult className="h-full" />
      </DashboardCard>
    );
  }

  const seeAllButton = (
    <Button variant="link" asChild>
      <Link to="workflows?tab=ongoing">{t("commons.buttons.seeAll")}</Link>
    </Button>
  );

  return (
    <DashboardCard title={containerTitle} button={seeAllButton}>
      <SimpleDataTable
        columns={tableColumns}
        data={activeWorkflows ?? []}
        onClickRow={(i) => navigate(`/documents/${i.document.id}?tab=workflow`)}
      />
    </DashboardCard>
  );
};

export default OngoingWorkflowsContainer;
